import { t } from './i18n';
import type { TranslationKey } from './i18n/types';
import type { PlayerArchetype, PlayerTilt } from './types';

/** Иконки типажей — общие для личной статистики и карточки игрока. */
export const ARCHETYPE_ICONS: Record<string, string> = {
  usurper: '⚡',
  magnate: '💰',
  fortress: '🏰',
  raider: '🏴',
  punisher: '⚔️',
  syndicate: '🤝',
  grandmaster: '♟️',
  titan: '👑',
  observer: '👁️',
  conqueror: '🏆',
  gladiator: '🗡️',
  vanguard: '🚩',
  skirmisher: '🏹',
  berserker: '🪓',
  guardian: '🛡️',
  tycoon: '💼',
  warden: '🧱',
  gambler: '🎲',
  citadel: '🏯',
  sentinel: '🗼',
  drifter: '🌪️',
  farmer: '🌾',
  trencher: '⛏️',
};

const TILT_ICONS: Record<PlayerTilt['level'], string> = {
  calm: '🧊',
  steady: '😌',
  variable: '🌤️',
  volatile: '🔥',
  tilted: '🌪️',
};

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

/** Блок типажа: архетип, три оси и личные черты из данных игр. */
export function buildArchetypeBlock(a: PlayerArchetype): HTMLElement {
  const wrap = node('div', 'mystats-archetype');

  const head = node('div', 'mystats-archetype-head');
  head.appendChild(node('span', 'mystats-archetype-icon', ARCHETYPE_ICONS[a.id] ?? '🎭'));
  const title = node('div', 'mystats-archetype-title');
  title.appendChild(
    node('h3', 'mystats-archetype-name', t(`mystats.arch.${a.id}.name` as TranslationKey)),
  );
  if (a.modifier) {
    title.appendChild(
      node('span', 'mystats-archetype-sub', t(`mystats.mod.${a.modifier}` as TranslationKey)),
    );
  }
  head.appendChild(title);
  const match = node('span', 'mystats-archetype-match');
  match.append(
    node('span', 'mystats-archetype-match-value', `${a.match}%`),
    node('span', 'mystats-archetype-match-label', t('mystats.arch_match')),
  );
  head.appendChild(match);
  wrap.appendChild(head);

  wrap.appendChild(
    node('p', 'mystats-archetype-desc', t(`mystats.arch.${a.id}.desc` as TranslationKey)),
  );

  // Ближайший типаж: профиль на границе клеток — показываем, куда ещё тянет.
  if (a.near) {
    wrap.appendChild(
      node(
        'p',
        'mystats-archetype-near',
        t('mystats.arch_near', {
          arch: t(`mystats.arch.${a.near.id}.name` as TranslationKey),
          match: a.near.match,
        }),
      ),
    );
  }

  const axes: Array<[keyof PlayerArchetype['axes'], TranslationKey]> = [
    ['aggression', 'mystats.arch_axis.aggression'],
    ['greed', 'mystats.arch_axis.greed'],
    ['turtle', 'mystats.arch_axis.turtle'],
  ];
  const bars = node('div', 'mystats-archetype-axes');
  for (const [key, labelKey] of axes) {
    const value = Number(a.axes[key]);
    const signal = a.signals?.[key] ?? null;
    const row = node('div', 'mystats-archetype-axis');
    const line = node('div', 'mystats-archetype-axis-head');
    line.append(
      node('span', '', t(labelKey)),
      node('span', 'mystats-archetype-axis-value', value.toFixed(1).replace('.', ',')),
    );
    const track = node('div', 'mystats-archetype-axis-track');
    const fill = node('div', `mystats-archetype-axis-fill is-${key}`);
    fill.style.width = `${Math.max(2, Math.min(100, value * 10))}%`;
    track.appendChild(fill);
    row.append(line, track);

    // Из чего сложилась ось: вклад оценок стиля и вклад игр + факты.
    if (signal && (signal.style != null || signal.behavior != null)) {
      const parts: string[] = [];
      if (signal.style != null) {
        parts.push(`${t('mystats.arch_part_style')} ${signal.style.toFixed(1).replace('.', ',')}`);
      }
      if (signal.behavior != null) {
        parts.push(`${t('mystats.arch_part_games')} ${signal.behavior.toFixed(1).replace('.', ',')}`);
      }
      row.appendChild(node('span', 'mystats-archetype-axis-split', parts.join(' · ')));
    }
    if (signal && signal.notes.length > 0) {
      const notes = node('div', 'mystats-archetype-axis-notes');
      for (const note of signal.notes) {
        notes.appendChild(
          node('span', 'mystats-archetype-axis-note', t(`mystats.signal.${note.id}` as TranslationKey, note.params)),
        );
      }
      row.appendChild(notes);
    }
    bars.appendChild(row);
  }
  wrap.appendChild(bars);

  if (a.traits.length > 0) {
    const traits = node('div', 'mystats-archetype-traits');
    for (const trait of a.traits) {
      traits.appendChild(
        node(
          'span',
          'mystats-archetype-trait',
          t(`mystats.trait.${trait.id}` as TranslationKey, trait.params),
        ),
      );
    }
    wrap.appendChild(traits);
  }

  if (a.tilt) wrap.appendChild(buildTiltBlock(a.tilt));

  wrap.appendChild(
    node(
      'p',
      'hint mystats-archetype-sources',
      t('mystats.arch_sources', { games: a.games, votes: a.votes }),
    ),
  );

  // Полный список типажей: описание, распределение и представители.
  const allLink = node('a', 'mystats-archetype-all', t('archetypes.open_all'));
  allLink.href = '/archetypes';
  wrap.appendChild(allLink);
  return wrap;
}

/** Индикатор тильта: шкала «спокоен → нестабилен» и из чего он сложился. */
function buildTiltBlock(tilt: PlayerTilt): HTMLElement {
  const wrap = node('div', 'mystats-tilt');

  const head = node('div', 'mystats-tilt-head');
  head.append(
    node('span', 'mystats-tilt-label', t('mystats.tilt_title')),
    node(
      'span',
      'mystats-tilt-level',
      `${TILT_ICONS[tilt.level] ?? '🎭'} ${t(`mystats.tilt_level.${tilt.level}` as TranslationKey)}`,
    ),
    node('span', 'mystats-tilt-score', String(tilt.score)),
  );
  wrap.appendChild(head);

  const scale = node('div', 'mystats-tilt-scale');
  const marker = node('span', 'mystats-tilt-marker');
  marker.style.left = `${Math.max(2, Math.min(98, tilt.score))}%`;
  scale.appendChild(marker);
  wrap.appendChild(scale);

  const legend = node('div', 'mystats-tilt-legend');
  legend.append(
    node('span', '', t('mystats.tilt_calm')),
    node('span', '', t('mystats.tilt_tilted')),
  );
  wrap.appendChild(legend);

  wrap.appendChild(
    node(
      'p',
      'mystats-tilt-caption',
      t(`mystats.tilt_desc.${tilt.level}` as TranslationKey),
    ),
  );

  const meta = node('div', 'mystats-tilt-meta');
  meta.append(
    node(
      'span',
      'mystats-tilt-chip',
      t('mystats.tilt.decay', { value: tilt.decay }),
    ),
    node(
      'span',
      'mystats-tilt-chip',
      t('mystats.tilt.breaks', { value: tilt.breaks }),
    ),
  );
  wrap.appendChild(meta);

  return wrap;
}
